// Procedure steps stored as JSON: [{instruction, params}]
// params is an optional freeform string (e.g. "82°C · 3 hrs · 15 PSI")

const EMPTY_STEP = () => ({ instruction: "", params: "" });

export function parseSteps(raw) {
  if (!raw) return [EMPTY_STEP()];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    // Legacy freeform text — split on newlines and treat each as a step
    const lines = raw.split("\n").map(l => l.trim()).filter(Boolean);
    if (lines.length > 0) return lines.map(l => ({ instruction: l, params: "" }));
  }
  return [EMPTY_STEP()];
}

export function serializeSteps(steps) {
  const clean = steps.filter(s => s.instruction.trim());
  if (clean.length === 0) return null;
  return JSON.stringify(clean);
}

// View mode: numbered step list
export function ProcedureSteps({ raw }) {
  if (!raw) return <p className="text-sm text-gray-400 italic">No procedure written.</p>;

  let steps = [];
  try {
    const parsed = JSON.parse(raw);
    steps = Array.isArray(parsed) ? parsed : [];
  } catch {
    // Legacy plain text
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{raw}</p>;
  }

  if (steps.length === 0) return <p className="text-sm text-gray-400 italic">No procedure written.</p>;

  return (
    <ol className="flex flex-col gap-3">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-gray-700">{step.instruction}</span>
            {step.params && (
              <span className="text-xs text-gray-400 font-mono">{step.params}</span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

// Edit mode: step row editor
export default function ProcedureEditor({ steps, onChange }) {
  function updateStep(i, field, value) {
    onChange(steps.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  }

  function addStep() {
    onChange([...steps, EMPTY_STEP()]);
  }

  function removeStep(i) {
    const next = steps.filter((_, idx) => idx !== i);
    onChange(next.length === 0 ? [EMPTY_STEP()] : next);
  }

  function moveStep(i, dir) {
    const next = [...steps];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-2 items-start">
          {/* Step number + reorder */}
          <div className="flex flex-col items-center gap-0.5 pt-1.5 shrink-0">
            <button
              type="button"
              onClick={() => moveStep(i, -1)}
              disabled={i === 0}
              className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs"
              aria-label="Move up"
            >▲</button>
            <span className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center">
              {i + 1}
            </span>
            <button
              type="button"
              onClick={() => moveStep(i, 1)}
              disabled={i === steps.length - 1}
              className="text-gray-300 hover:text-gray-500 disabled:opacity-20 leading-none text-xs"
              aria-label="Move down"
            >▼</button>
          </div>

          {/* Instruction + params */}
          <div className="flex flex-col gap-1.5 flex-1">
            <textarea
              value={step.instruction}
              onChange={e => updateStep(i, "instruction", e.target.value)}
              rows={2}
              placeholder="Describe this step…"
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
            />
            <input
              type="text"
              value={step.params}
              onChange={e => updateStep(i, "params", e.target.value)}
              placeholder="Parameters (optional) e.g. 82°C · 3 hrs · 15 PSI"
              className="border border-gray-200 rounded px-2 py-1 text-xs font-mono text-gray-500 focus:outline-none focus:ring-1 focus:ring-sky-400 bg-gray-50"
            />
          </div>

          {/* Remove */}
          <button
            type="button"
            onClick={() => removeStep(i)}
            className="text-gray-300 hover:text-red-400 text-lg leading-none font-light pt-1.5 shrink-0 transition-colors"
            aria-label="Remove step"
          >×</button>
        </div>
      ))}

      <button
        type="button"
        onClick={addStep}
        className="text-sm text-sky-700 hover:text-sky-800 text-left mt-1 font-medium"
      >
        + Add step
      </button>
    </div>
  );
}
